import type React from "react";
import { useState } from "react";
import { PostCard, type PostCardViewModel } from "@/components/common/PostCard";
import { EventLinkPreviewProvider } from "@/contexts/EventLinkPreviewContext";
import { CommentSection } from "@/features/study-group-stream/components/CommentSection";
import { useCommentToggleFallback } from "@/features/study-group-stream/hooks/useCommentToggleFallback";
import type { EventApiResponse } from "@/lib/humhub-api/types";
import { cn } from "@/lib/utils";

interface PostThreadProps {
  postCard: PostCardViewModel;
  commentSectionProps: React.ComponentProps<typeof CommentSection>;
  eventLinkPreviewProps?: {
    onEventClick?: (eventData: EventApiResponse) => void;
    currentStudyGroupId?: string;
  };
  className?: string;
}

export const PostThread: React.FC<PostThreadProps> = ({
  postCard,
  commentSectionProps,
  eventLinkPreviewProps,
  className,
}) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const {
    isCommentActive,
    isComposerOpen,
    commentSectionRef,
    handleCommentToggle,
    closeComposer,
  } = useCommentToggleFallback({
    onCommentToggle: postCard.handlers.onCommentToggle,
  });
  const fallbackControlledComposer = !postCard.handlers.onCommentToggle;
  const threadBackgroundClass = postCard.status.isHighlighted
    ? "bg-amber-50"
    : "stream-bg-post";

  return (
    <div
      className={cn(
        "w-full overflow-visible rounded-tr-2xl border border-gray-200",
        threadBackgroundClass,
        className,
      )}
    >
      <PostCard
        {...postCard}
        status={{
          ...postCard.status,
          isCommentActive: postCard.status.isCommentActive || isCommentActive,
          isMinimized,
        }}
        handlers={{
          ...postCard.handlers,
          onCommentToggle: handleCommentToggle,
          onToggleMinimize: () => setIsMinimized((prev) => !prev),
        }}
      />
      {!isMinimized && (
        <div ref={commentSectionRef}>
          <EventLinkPreviewProvider
            onEventClick={eventLinkPreviewProps?.onEventClick}
            currentStudyGroupId={eventLinkPreviewProps?.currentStudyGroupId}
          >
            <CommentSection
              {...commentSectionProps}
              openComposer={
                fallbackControlledComposer
                  ? isComposerOpen
                  : commentSectionProps.openComposer
              }
              onCommentSent={
                fallbackControlledComposer
                  ? () => {
                      commentSectionProps.onCommentSent?.();
                      closeComposer();
                    }
                  : commentSectionProps.onCommentSent
              }
            />
          </EventLinkPreviewProvider>
        </div>
      )}
    </div>
  );
};
